import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ClassContext } from '../context/ClassContext';
import { AuthContext } from '../context/AuthContext';
import BlogList from '../components/BlogList';
import BulkEnrollStudents from '../components/BulkEnrollStudents';
import Pagination from '../components/Pagination';

const ClassDetails = () => {
    const { classId } = useParams();
    const { user } = useContext(AuthContext);
    const {
        currentClass, students, documents,
        getClassById, getAllStudents, enrollStudent,
        getDocumentsForClass, uploadDocument, loading,
        deleteDocument, renameDocument, removeStudentFromClass
    } = useContext(ClassContext);

    const [selectedStudent, setSelectedStudent] = useState('');
    const [file, setFile] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [activeTab, setActiveTab] = useState('documents');
    const [renameModal, setRenameModal] = useState({ isOpen: false, document: null, newName: '' });
    const fileInputRef = useRef(null);
    const [studentPage, setStudentPage] = useState(1);
    const [studentsPerPage] = useState(10);

    const formatSchedules = (schedules) => {
        if (!schedules || schedules.length === 0) return 'Chưa có lịch học';

        const dayLabels = {
            'monday': 'Thứ Hai',
            'tuesday': 'Thứ Ba',
            'wednesday': 'Thứ Tư',
            'thursday': 'Thứ Năm',
            'friday': 'Thứ Sáu',
            'saturday': 'Thứ Bảy',
            'sunday': 'Chủ Nhật'
        };

        return schedules.map(schedule =>
            `${dayLabels[schedule.dayOfWeek]} ${schedule.startTime}-${schedule.endTime}`
        ).join(', ');
    };

    useEffect(() => {
        getClassById(classId);
        getDocumentsForClass(classId);
        if (user?.role === 'admin') {
            getAllStudents();
        }
    }, [classId, getAllStudents, getClassById, getDocumentsForClass, user]);

    const onFileChange = e => {
        setFile(e.target.files[0]);
    };

    const onFileUpload = e => {
        e.preventDefault();
        if (!file) return;
        uploadDocument(classId, file);
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const onDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            setFile(files[0]);
        }
    };

    const onEnroll = (e) => {
        e.preventDefault();
        if (selectedStudent) {
            enrollStudent(classId, selectedStudent);
            setSelectedStudent('');
        }
    };

    const onDeleteDocument = (documentId) => {
        if (window.confirm('Bạn có chắc chắn muốn xóa tài liệu này?')) {
            deleteDocument(documentId);
        }
    };

    const onRemoveStudent = (studentId) => {
        if (window.confirm('Bạn có chắc chắn muốn loại bỏ học sinh này khỏi lớp?')) {
            removeStudentFromClass(classId, studentId);
        }
    };

    const onRenameDocument = (document) => {
        setRenameModal({
            isOpen: true,
            document: document,
            newName: document.fileName
        });
    };

    const handleRenameSubmit = () => {
        if (renameModal.newName.trim() && renameModal.document) {
            renameDocument(renameModal.document._id, renameModal.newName.trim());
            setRenameModal({ isOpen: false, document: null, newName: '' });
        }
    };

    const closeRenameModal = () => {
        setRenameModal({ isOpen: false, document: null, newName: '' });
    };

    const getFileIcon = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        switch (extension) {
            case 'pdf': return '📄';
            case 'doc':
            case 'docx': return '📝';
            case 'ppt':
            case 'pptx': return '📊';
            case 'xls':
            case 'xlsx': return '📈';
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif': return '🖼️';
            case 'mp4':
            case 'avi':
            case 'mov': return '🎥';
            case 'mp3':
            case 'wav': return '🎵';
            default: return '📎';
        }
    };

    if (loading || !currentClass || (user?.role === 'admin' && !students)) {
        return (
            <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 rounded-3xl bg-white/70 p-12 text-center shadow-xl backdrop-blur">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-500" aria-hidden />
                <p className="text-lg font-medium text-slate-700">Đang tải thông tin lớp học...</p>
            </div>
        );
    }

    const availableStudents = user?.role === 'admin' ? students.filter(
        (student) => !currentClass.students.some((enrolled) => enrolled._id === student._id)
    ) : [];

    return (
        <div className="space-y-10 rounded-3xl bg-white/85 p-8 shadow-xl backdrop-blur">
            <div className="flex flex-col gap-6 rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 p-8 text-white shadow-lg">
                <Link to="/dashboard" className="inline-flex w-fit items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-medium transition hover:bg-white/25">
                    ← Quay lại Dashboard
                </Link>
                <div className="space-y-4">
                    <h1 className="text-3xl font-semibold">📚 {currentClass.name}</h1>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="flex items-center gap-3 rounded-2xl bg-white/15 px-4 py-3 text-sm">
                            <span className="text-xl" aria-hidden>📅</span>
                            <span>Lịch học: {formatSchedules(currentClass.schedules || []) || currentClass.schedule}</span>
                        </div>
                        <div className="flex items-center gap-3 rounded-2xl bg-white/15 px-4 py-3 text-sm">
                            <span className="text-xl" aria-hidden>👥</span>
                            <span>{currentClass.students.length} học sinh</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 rounded-xl bg-white/70 p-2 shadow-lg backdrop-blur">
                <button
                    onClick={() => setActiveTab('documents')}
                    className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                        activeTab === 'documents'
                            ? 'bg-indigo-600 text-white shadow'
                            : 'bg-white/50 text-slate-700 hover:bg-white/80'
                    }`}
                >
                    📄 Tài liệu
                </button>
                <button
                    onClick={() => setActiveTab('blogs')}
                    className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                        activeTab === 'blogs'
                            ? 'bg-indigo-600 text-white shadow'
                            : 'bg-white/50 text-slate-700 hover:bg-white/80'
                    }`}
                >
                    📚 Lý thuyết
                </button>
                <button
                    onClick={() => setActiveTab('students')}
                    className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                        activeTab === 'students'
                            ? 'bg-indigo-600 text-white shadow'
                            : 'bg-white/50 text-slate-700 hover:bg-white/80'
                    }`}
                >
                    👥 Học sinh ({currentClass.students.length})
                </button>
            </div>

            {activeTab === 'documents' && (
                <div className="space-y-6">
                    <div className="space-y-6 rounded-3xl border border-slate-100 bg-white/90 p-6 shadow-lg">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <h3 className="flex items-center gap-3 text-2xl font-semibold text-slate-900">
                                <span aria-hidden>📚</span>
                                Tài liệu học tập
                            </h3>
                            {documents && documents.length > 0 && (
                                <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
                                    {documents.length} tài liệu
                                </span>
                            )}
                        </div>

                        {user?.role === 'admin' && (
                            <form onSubmit={onFileUpload} className="space-y-4">
                                <div
                                    className={`flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed px-6 py-10 text-center text-sm transition ${isDragOver ? 'border-indigo-400 bg-indigo-50/70 text-indigo-600' : 'border-slate-200 bg-slate-50/60 text-slate-500'}`}
                                    onDragOver={onDragOver}
                                    onDragLeave={onDragLeave}
                                    onDrop={onDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <span className="text-3xl" aria-hidden>📁</span>
                                    <p>
                                        {file ? (
                                            <>Đã chọn: <strong>{file.name}</strong></>
                                        ) : (
                                            <>Kéo thả tệp vào đây hoặc <strong>nhấp để chọn</strong></>
                                        )}
                                    </p>
                                    <input
                                        type="file"
                                        onChange={onFileChange}
                                        className="hidden"
                                        ref={fileInputRef}
                                        accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.mp4,.avi,.mov,.mp3,.wav"
                                    />
                                </div>
                                {file && (
                                    <button type="submit" className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-600">
                                        🚀 Tải lên tài liệu
                                    </button>
                                )}
                            </form>
                        )}

                        <div className="grid gap-4 md:grid-cols-2">
                            {documents && documents.length > 0 ? (
                                documents.map((doc) => (
                                    <div key={doc._id} className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white/80 p-5 shadow">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-2xl">
                                            {getFileIcon(doc.fileName)}
                                        </div>
                                        <div className="flex flex-1 flex-col gap-2 text-sm">
                                            <h4 className="font-semibold text-slate-900">{doc.fileName}</h4>
                                            <p className="text-xs text-slate-500">📅 {new Date(doc.uploadDate).toLocaleDateString('vi-VN')}</p>
                                            <div className="flex flex-wrap gap-2">
                                                <a
                                                    href={`${process.env.REACT_APP_BACKEND_URL.replace('/api', '')}/${doc.filePath}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex w-fit items-center gap-2 rounded-lg bg-indigo-600 px-3 py-1 text-xs font-semibold text-white transition hover:bg-indigo-700"
                                                >
                                                    ⬇️ Tải xuống
                                                </a>
                                                {user?.role === 'admin' && (
                                                    <>
                                                        <button
                                                            onClick={() => onRenameDocument(doc)}
                                                            className="inline-flex w-fit items-center gap-2 rounded-lg bg-amber-600 px-3 py-1 text-xs font-semibold text-white transition hover:bg-amber-700"
                                                        >
                                                            ✏️ Đổi tên
                                                        </button>
                                                        <button
                                                            onClick={() => onDeleteDocument(doc._id)}
                                                            className="inline-flex w-fit items-center gap-2 rounded-lg bg-red-600 px-3 py-1 text-xs font-semibold text-white transition hover:bg-red-700"
                                                        >
                                                            🗑️ Xóa
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="flex flex-col items-center gap-3 rounded-2xl bg-slate-50 p-10 text-center text-sm text-slate-600 md:col-span-2">
                                    <span className="text-3xl" aria-hidden>📚</span>
                                    <h4 className="text-lg font-semibold text-slate-900">Chưa có tài liệu nào</h4>
                                    <p>
                                        {user?.role === 'admin'
                                            ? 'Hãy tải lên tài liệu đầu tiên cho lớp học này.'
                                            : 'Giáo viên chưa tải lên tài liệu nào cho lớp học này.'}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'blogs' && (
                <div className="space-y-6">
                    <BlogList classId={classId} />
                </div>
            )}

            {activeTab === 'students' && (
                <div className="space-y-6">
                    {user?.role === 'admin' && (
                        <>
                            {/* Single Student Enrollment */}
                            <div className="rounded-3xl border border-slate-100 bg-white/90 p-6 shadow-lg">
                                <div className="mb-4 flex items-center gap-3 text-xl font-semibold text-slate-900">
                                    <span aria-hidden>👋</span>
                                    Đăng ký học sinh (từng người)
                                </div>
                                {availableStudents.length > 0 ? (
                                    <form onSubmit={onEnroll} className="space-y-4">
                                        <label className="flex flex-col gap-2 text-sm font-medium text-slate-600">
                                            Chọn học sinh để đăng ký
                                            <select
                                                value={selectedStudent}
                                                onChange={(e) => setSelectedStudent(e.target.value)}
                                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                            >
                                                <option value="">-- Chọn học sinh --</option>
                                                {availableStudents.map((student) => (
                                                    <option key={student._id} value={student._id}>
                                                        {student.name} ({student.email})
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <button
                                            type="submit"
                                            disabled={!selectedStudent}
                                            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                                        >
                                            ✅ Đăng ký học sinh
                                        </button>
                                    </form>
                                ) : (
                                    <div className="flex flex-col items-center gap-3 rounded-2xl bg-slate-50 p-6 text-center text-sm text-slate-600">
                                        <span className="text-3xl" aria-hidden>✅</span>
                                        <h4 className="text-lg font-semibold text-slate-900">Tất cả học sinh đã được đăng ký</h4>
                                        <p>Không còn học sinh nào có thể đăng ký thêm vào lớp này.</p>
                                    </div>
                                )}
                            </div>

                            {/* Bulk Enrollment */}
                            <BulkEnrollStudents
                                classId={classId}
                                onSuccess={() => {
                                    getClassById(classId);
                                    getAllStudents();
                                }}
                            />
                        </>
                    )}

                    {/* Student List */}
                    <div className="rounded-3xl border border-slate-100 bg-white/90 p-6 shadow-lg">
                        <div className="mb-6 flex items-center justify-between">
                            <h3 className="flex items-center gap-3 text-2xl font-semibold text-slate-900">
                                <span aria-hidden>👥</span>
                                Danh sách học sinh
                            </h3>
                            <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
                                {currentClass.students.length} học sinh
                            </span>
                        </div>

                        <div className="space-y-4">
                            {currentClass.students.length > 0 ? (
                                (() => {
                                    const totalStudentPages = Math.ceil(currentClass.students.length / studentsPerPage);
                                    const indexOfLastStudent = studentPage * studentsPerPage;
                                    const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
                                    const currentStudents = currentClass.students.slice(indexOfFirstStudent, indexOfLastStudent);

                                    return (
                                        <>
                                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                                {currentStudents.map((student) => (
                                                    <div key={student._id} className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white/80 p-5 shadow-sm transition hover:shadow-md">
                                                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-indigo-100 text-2xl text-indigo-600">
                                                            👤
                                                        </div>
                                                        <div className="flex-1 space-y-2">
                                                            <h4 className="font-semibold text-slate-900">{student.name}</h4>
                                                            <p className="text-xs text-slate-500">{student.email}</p>
                                                            <div className="flex flex-wrap gap-2">
                                                                <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                                                                    ✅ Đã đăng ký
                                                                </span>
                                                                {user?.role === 'admin' && (
                                                                    <button
                                                                        onClick={() => onRemoveStudent(student._id)}
                                                                        className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700 transition hover:bg-red-200"
                                                                    >
                                                                        🗑️ Loại bỏ
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {currentClass.students.length > studentsPerPage && (
                                                <div className="mt-6">
                                                    <Pagination
                                                        currentPage={studentPage}
                                                        totalPages={totalStudentPages}
                                                        onPageChange={setStudentPage}
                                                        itemsPerPage={studentsPerPage}
                                                        totalItems={currentClass.students.length}
                                                    />
                                                </div>
                                            )}
                                        </>
                                    );
                                })()
                            ) : (
                                <div className="flex flex-col items-center gap-3 rounded-2xl bg-slate-50 p-10 text-center text-sm text-slate-600">
                                    <span className="text-3xl" aria-hidden>👥</span>
                                    <h4 className="text-lg font-semibold text-slate-900">Chưa có học sinh nào</h4>
                                    <p>
                                        {user?.role === 'admin'
                                            ? 'Hãy đăng ký học sinh đầu tiên cho lớp học này.'
                                            : 'Lớp học này chưa có học sinh đăng ký.'}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Rename Document Modal */}
            {renameModal.isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
                        <div className="mb-4 flex items-center gap-3">
                            <span className="text-2xl">✏️</span>
                            <h3 className="text-lg font-semibold text-slate-900">Đổi tên tài liệu</h3>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Tên tài liệu mới
                                </label>
                                <input
                                    type="text"
                                    value={renameModal.newName}
                                    onChange={(e) => setRenameModal(prev => ({ ...prev, newName: e.target.value }))}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                    placeholder="Nhập tên mới cho tài liệu..."
                                    onKeyDown={(e) => e.key === 'Enter' && handleRenameSubmit()}
                                />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={closeRenameModal}
                                    className="flex-1 rounded-xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
                                >
                                    Hủy
                                </button>
                                <button
                                    onClick={handleRenameSubmit}
                                    disabled={!renameModal.newName.trim()}
                                    className="flex-1 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                                >
                                    Đổi tên
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClassDetails;
